import { useEffect, useState, useCallback } from "react";
import {
  Plus, Plug, MessageSquare, List, Pencil, Trash2, Zap, CheckCircle2, XCircle, Copy,
} from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import AddProviderDialog from "../components/AddProviderDialog";
import ManageModelsDialog from "../components/ManageModelsDialog";
import TestProviderDialog from "../components/TestProviderDialog";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";

export default function ProvidersPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [manageFor, setManageFor] = useState(null);
  const [testingFor, setTestingFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/providers");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(apiErrorMessage(e));
      setList([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function del(id) {
    if (!window.confirm(`¿Eliminar el proveedor "${id}" y su API key cifrada?`)) return;
    try {
      await api.delete(`/providers/${encodeURIComponent(id)}`);
      toast.success("Proveedor eliminado");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }

  async function test(p) {
    try {
      const { data } = await api.get(`/providers/${encodeURIComponent(p.id)}/test`);
      toast.success(data.ok ? "Conexión OK" : `Sin respuesta: ${data.message || "?"}`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }

  function copyEndpoint(id) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/providers/${id}/chat`;
    navigator.clipboard.writeText(url).then(() => toast.success("Endpoint copiado"));
  }

  return (
    <div data-testid={TID.providers.root}>
      <PageHeader
        eyebrow="IA"
        title="Proveedores IA"
        description="Agregadores REST (OpenRouter, NVIDIA NIM, Groq, Together, Fireworks…). Un solo endpoint para tus apps; KRAI API guarda las API keys cifradas."
        actions={
          <Button data-testid={TID.providers.addButton}
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-white text-black hover:bg-zinc-200 h-9">
            <Plus className="h-4 w-4 mr-1.5" /> Nuevo proveedor
          </Button>
        }
      />

      <div className="mb-6 rounded-md border border-zinc-800 bg-zinc-950 p-4 flex items-start gap-3 text-xs">
        <Plug className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
        <div className="text-zinc-400 leading-relaxed">
          Formato unificado <b className="text-zinc-200">OpenAI-compatible</b>. Tus apps hacen:
          <code className="ml-1 font-mono text-zinc-300 bg-zinc-900 px-1.5 py-0.5 rounded">
            POST /api/providers/{"{id}"}/chat  {"{ model, messages, stream:false }"}
          </code>
        </div>
      </div>

      {loading ? (
        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Cargando proveedores…
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-900/40 bg-red-950/20 text-red-300 text-xs p-3">
          {error}
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Aún no hay proveedores. Añade uno con "Nuevo proveedor" (hay presets listos).
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((p) => (
            <ProviderCard
              key={p.id} provider={p}
              onTest={() => test(p)}
              onChat={() => setTestingFor(p)}
              onManage={() => setManageFor(p)}
              onEdit={() => { setEditing(p); setDialogOpen(true); }}
              onDelete={() => del(p.id)}
              onCopy={() => copyEndpoint(p.id)}
            />
          ))}
        </div>
      )}

      <AddProviderDialog
        key={editing?.id || "new"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={load}
      />
      <ManageModelsDialog
        open={Boolean(manageFor)}
        onOpenChange={(v) => !v && setManageFor(null)}
        provider={manageFor}
        onSaved={load}
      />
      <TestProviderDialog
        open={Boolean(testingFor)}
        onOpenChange={(v) => !v && setTestingFor(null)}
        provider={testingFor}
      />
    </div>
  );
}

function ProviderCard({ provider, onTest, onChat, onManage, onEdit, onDelete, onCopy }) {
  return (
    <div
      data-testid={TID.providers.card(provider.id)}
      className="rounded-md border border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden hover:border-zinc-700 transition-colors duration-150"
    >
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md border border-zinc-800 bg-zinc-900 grid place-items-center">
            <Plug className="h-4 w-4 text-zinc-300" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{provider.name}</div>
            <div className="text-[11px] font-mono text-zinc-500 truncate">id: {provider.id}</div>
          </div>
        </div>
        {provider.has_api_key ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 shrink-0">
            <CheckCircle2 className="h-3 w-3" /> key
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 shrink-0">
            <XCircle className="h-3 w-3" /> sin key
          </span>
        )}
      </div>

      <div className="px-5 pb-4 space-y-3">
        <MetaRow label="Base URL">
          <span className="font-mono text-xs text-zinc-300 truncate block">{provider.base_url}</span>
        </MetaRow>
        <div className="grid grid-cols-2 gap-3">
          <MetaRow label="Auth">
            <span className="font-mono text-xs text-zinc-300">{provider.auth_style}</span>
          </MetaRow>
          <MetaRow label="Modelos">
            <span className="font-mono text-xs text-zinc-300">
              {provider.models?.length || 0} configurados
            </span>
          </MetaRow>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <MetaRow label="Endpoint">
            <button onClick={onCopy}
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
              title="Copiar URL del endpoint">
              <Copy className="h-3 w-3" /> copiar
            </button>
          </MetaRow>
          <MetaRow label="Key">
            <span className="font-mono text-xs text-zinc-400">
              {provider.api_key_masked || "—"}
            </span>
          </MetaRow>
        </div>
      </div>

      <div className="mt-auto border-t border-zinc-800 p-3 flex flex-wrap items-center gap-2">
        <Button size="sm" data-testid={TID.providers.chat(provider.id)}
          onClick={onChat} disabled={!provider.has_api_key || !(provider.models?.length)}
          className="bg-white text-black hover:bg-zinc-200 h-8 disabled:opacity-40">
          <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Probar chat
        </Button>
        <Button size="sm" variant="secondary" data-testid={TID.providers.test(provider.id)}
          onClick={onTest} disabled={!provider.has_api_key}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 disabled:opacity-40">
          <Zap className="h-3.5 w-3.5 mr-1.5" /> Test
        </Button>
        <Button size="sm" variant="secondary" data-testid={TID.providers.models(provider.id)}
          onClick={onManage}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200">
          <List className="h-3.5 w-3.5 mr-1.5" /> Modelos
        </Button>
        <Button size="sm" variant="secondary" data-testid={TID.providers.edit(provider.id)}
          onClick={onEdit}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <button data-testid={TID.providers.delete(provider.id)}
          onClick={onDelete}
          className="ml-auto h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors duration-150"
          title="Eliminar">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="space-y-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        {label}
      </div>
      {children}
    </div>
  );
}
